export const LAYERING_MODE_BEST = 'best'
export const LAYERING_MODE_SURPRISE = 'surprise'
export const MAX_LAYERING_PARTNERS = 5

function normalized(value) {
  return String(value || '').trim().toLocaleLowerCase()
}

export function layeringPartnerKey(pairing) {
  if (pairing?.catalog_id) return `catalog:${pairing.catalog_id}`
  return `label:${normalized(pairing?.house)}::${normalized(pairing?.name)}::${normalized(pairing?.concentration)}`
}

function finiteNumber(value) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function rawLayeringScore(pairing, fallback = 0) {
  return finiteNumber(pairing?.compatibility_score)
    ?? finiteNumber(pairing?.score)
    ?? finiteNumber(pairing?.raw_score)
    ?? fallback
}

export function layeringPopularityPenalty(count = 1) {
  return 0.8 * Math.log2(Math.max(1, finiteNumber(count) ?? 1))
}

export function buildLayeringPopularityCounts(pairingRows = []) {
  const counts = new Map()
  for (const row of pairingRows) {
    const cards = Array.isArray(row?.suggested_pairings) ? row.suggested_pairings : []
    const scored = cards.map((pairing, index) => ({
      pairing,
      score: rawLayeringScore(pairing, cards.length - index),
    }))
    if (scored.length === 0) continue
    const max = Math.max(...scored.map(item => item.score))
    for (const { pairing, score } of scored) {
      if (score < max - 1.5) continue
      const key = layeringPartnerKey(pairing)
      counts.set(key, (counts.get(key) || 0) + 1)
    }
  }
  return counts
}

function seededRandom(seedText) {
  let state = 2166136261
  for (const character of String(seedText)) {
    state ^= character.charCodeAt(0)
    state = Math.imul(state, 16777619)
  }
  return () => {
    state += 0x6D2B79F5
    let value = state
    value = Math.imul(value ^ value >>> 15, value | 1)
    value ^= value + Math.imul(value ^ value >>> 7, value | 61)
    return ((value ^ value >>> 14) >>> 0) / 4294967296
  }
}

function shuffle(items, seed) {
  const shuffled = [...items]
  const random = seededRandom(seed)
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1))
    ;[shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]]
  }
  return shuffled
}

export function rankLayeringRecommendations({
  recommendations = [],
  popularityCounts = new Map(),
  mode = LAYERING_MODE_BEST,
  sessionSeed = '',
  limit = MAX_LAYERING_PARTNERS,
} = {}) {
  const list = Array.isArray(recommendations) ? recommendations : []
  const ranked = list.map((pairing, index) => {
    const key = layeringPartnerKey(pairing)
    const raw = rawLayeringScore(pairing, list.length - index)
    const popularity = finiteNumber(pairing?.popularity_count)
      ?? finiteNumber(popularityCounts.get(key))
      ?? 1
    const penalty = layeringPopularityPenalty(popularity)
    const score = mode === LAYERING_MODE_SURPRISE
      ? raw + penalty * 0.8
      : raw - penalty
    return { pairing, key, score }
  }).sort((left, right) => right.score - left.score || left.key.localeCompare(right.key))

  if (ranked.length === 0) return []
  const max = ranked[0].score
  const topTier = ranked.filter(item => item.score >= max - 1)
  const remainder = ranked.filter(item => item.score < max - 1)
  const shuffledTopTier = shuffle(
    topTier,
    `${sessionSeed}:${mode}:${topTier.map(item => item.key).join('|')}`,
  )
  return [...shuffledTopTier, ...remainder]
    .slice(0, Math.max(0, Math.trunc(Number(limit) || 0)))
    .map(item => item.pairing)
}
